'use client';

import { Carousel } from '@packages/modules/src/Carousel/Carousel';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { RelatedContentItem } from './RelatedContentItem.client';
import type { RelatedContentType } from './types';
import { useRelatedContentGql } from './useRelatedContentGql';

/**
 * RelatedContent Component
 *
 * Displays a carousel of related content items based on taxonomy terms.
 * This component fetches magazine content using GraphQL and renders it in
 * a horizontal scrollable carousel format.
 *
 * @component
 * @param {Object} props - Component props
 * @param {RelatedContentType} props.data - Configuration data for the related content
 * @param {number} props.data.field_number_of_hits - Maximum number of content items to display
 * @param {TaxonomyTerm[]} props.data.taxonomy_term - Array of taxonomy terms used to filter related content
 * @param {string} [props.data.behavior_paragraph_background_color] - Background color for the carousel
 * @param {string} [props.data.behavior_paragraph_outer_spacing] - Margin spacing for the component
 * @param {string} [props.className] - Additional CSS classes to apply to the wrapper
 *
 * @returns {JSX.Element | null} Rendered carousel with related content items or null if error occurs
 *
 * @example
 * ```tsx
 * <RelatedContent
 *   data={{
 *     field_number_of_hits: 6,
 *     taxonomy_term: [{ name: 'technology' }],
 *     behavior_paragraph_background_color: '#f5f5f5',
 *     behavior_paragraph_outer_spacing: '16px'
 *   }}
 *   className="my-4"
 * />
 * ```
 */
export const RelatedContent = ({
  data: {
    field_number_of_hits: limit,
    taxonomy_term,
    behavior_paragraph_background_color: backgroundColor,
    behavior_paragraph_outer_spacing: margin,
  },
  className,
}: {
  data: RelatedContentType;
  className?: string;
}) => {
  const { fetching, relatedContentData, error } = useRelatedContentGql(
    taxonomy_term.map((tag) => tag.name),
    limit,
  );
  if (error) {
    return null;
  }

  const visibleItemsCssProps =
    '[--items-visible:2.15] [--reco-gap:0.375rem] @xl:[--items-visible:3.5] @4xl:[--items-visible:4.5]';

  return (
    // className contains possible width type by behavior_paragraph_widthtype
    // - it must be applied to a wrapper div to not interfere with Carousel inner styles
    <div className={className}>
      <Carousel
        listClassName={visibleItemsCssProps}
        style={{ backgroundColor, ...(margin && { margin }) }}
      >
        {fetching &&
          Array.from({ length: limit }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton key={index} className="h-[23rem]" aria-label="loading" />
          ))}
        {relatedContentData
          .filter((item) => !!item)
          .map((item) => (
            <RelatedContentItem key={item.id} {...item} />
          ))}
      </Carousel>
    </div>
  );
};
